import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { MapPin, Sun, ExternalLink, Clock3 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SOURCE_URL = "https://www.radiomadreterra.it/WeekSchedule.html";

const FALLBACK_ITEMS = [
  { time: "01:00", title: "S. Rosario" },
  { time: "06:30", title: "Recita delle Lodi", location: "Porziuncola · Assisi" },
  { time: "07:00", title: "S. Messa", location: "Santuario di Caravaggio" },
  { time: "10:00", title: "S. Messa", location: "Santuario di Caravaggio" },
  { time: "11:00", title: "Punto d'incontro", subtitle: "a cura di Bruno Negri" },
  { time: "14:30", title: "S. Rosario" },
  { time: "15:00", title: "Coroncina alla Divina Misericordia" },
  { time: "16:00", title: "S. Messa", location: "Santuario di Caravaggio" },
  { time: "17:00", title: "S. Rosario", location: "Porziuncola · Assisi" },
  { time: "18:00", title: "S. Messa", location: "Porziuncola · Assisi" },
  { time: "19:00", title: "Recita dei Vespri", location: "Porziuncola · Assisi" },
  { time: "20:00", title: "S. Rosario" },
  { time: "22:00", title: "S. Rosario" },
];

function toMinutes(t) {
  const [h, m] = t.split(":").map((n) => parseInt(n, 10));
  return h * 60 + (m || 0);
}

function findCurrentIdx(items, nowMin) {
  if (!items.length) return -1;
  let current = -1;
  for (let i = 0; i < items.length; i++) {
    if (toMinutes(items[i].time) <= nowMin) current = i;
    else break;
  }
  return current >= 0 ? current : items.length - 1;
}

/**
 * Returns information about the next scheduled program (the one airing after
 * the current one). Wraps around midnight when the current program is the
 * day's last entry. Result: { item, minutesUntil } or null.
 */
function findNextProgram(items, currentIdx, nowMin) {
  if (!items.length) return null;
  const firstMin = toMinutes(items[0].time);
  // Before the day's first scheduled program → next is items[0] today.
  if (nowMin < firstMin) {
    return { item: items[0], minutesUntil: firstMin - nowMin };
  }
  let nextIdx;
  let crossesMidnight = false;
  if (currentIdx >= 0 && currentIdx < items.length - 1) {
    nextIdx = currentIdx + 1;
  } else {
    nextIdx = 0;
    crossesMidnight = true;
  }
  const nextItem = items[nextIdx];
  const nextMin = toMinutes(nextItem.time) + (crossesMidnight ? 24 * 60 : 0);
  const minutesUntil = nextMin - nowMin;
  if (minutesUntil <= 0) return null;
  return { item: nextItem, minutesUntil };
}

function formatCountdown(mins) {
  if (mins < 60) return `Tra ${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (m === 0) return `Tra ${h}h`;
  return `Tra ${h}h ${m}m`;
}

export const Schedule = () => {
  const [items, setItems] = useState(FALLBACK_ITEMS);
  const [weekdayName, setWeekdayName] = useState(null);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/schedule`)
      .then((res) => {
        if (cancelled || !res.data) return;
        const data = res.data;
        if (Array.isArray(data?.items) && data.items.length) {
          setItems(data.items);
          setWeekdayName(data.weekday_name || null);
        } else if (Array.isArray(data) && data.length) {
          // legacy shape — keep working if backend returns a plain list
          setItems(data);
        }
      })
      .catch(() => {
        /* keep fallback */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);

  const nowMin = now.getHours() * 60 + now.getMinutes();
  const isSunday = now.getDay() === 0;
  const currentIdx = useMemo(() => findCurrentIdx(items, nowMin), [items, nowMin]);
  const nextProgram = useMemo(
    () => findNextProgram(items, currentIdx, nowMin),
    [items, currentIdx, nowMin]
  );

  const headingDate = now.toLocaleDateString("it-IT", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  return (
    <section
      data-testid="schedule"
      className="relative overflow-hidden rounded-2xl border p-5 sm:p-7"
      style={{
        borderColor: "var(--mt-line)",
        background:
          "linear-gradient(135deg, rgba(20,17,13,0.78) 0%, rgba(28,24,19,0.55) 100%)",
        backdropFilter: "blur(14px)",
      }}
    >
      <div className="flex items-center justify-between mb-5 sm:mb-6 flex-wrap gap-3">
        <div>
          <h3
            className="font-display text-xl sm:text-2xl leading-tight"
            style={{ color: "var(--mt-cream)" }}
          >
            {headingDate}
          </h3>
          <p
            className="text-[11px] uppercase tracking-[0.22em]"
            style={{ color: "var(--mt-text-muted)" }}
          >
            Programmi in onda · 24 ore
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isSunday && (
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.22em] font-semibold"
              style={{
                background: "rgba(212,160,91,0.15)",
                color: "var(--mt-amber-bright)",
                border: "1px solid rgba(212,160,91,0.30)",
              }}
            >
              <Sun size={11} strokeWidth={2.4} />
              Programma domenicale
            </span>
          )}
          <a
            href={SOURCE_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="schedule-source-link"
            className="mt-btn inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.22em] font-semibold"
            style={{
              background: "rgba(20,17,13,0.6)",
              color: "var(--mt-cream)",
              border: "1px solid rgba(245,239,228,0.18)",
            }}
            title="Apri il palinsesto settimanale completo"
          >
            Settimana completa
            <ExternalLink size={11} strokeWidth={2.4} />
          </a>
        </div>
      </div>

      <ol className="grid sm:grid-cols-2 gap-2 sm:gap-3">
        {items.map((it, i) => {
          const isCurrent = i === currentIdx;
          return (
            <li
              key={`${it.time}-${i}`}
              data-testid={isCurrent ? "schedule-current" : "schedule-item"}
              className="flex items-start gap-3 rounded-xl border px-3.5 py-2.5"
              style={{
                borderColor: isCurrent
                  ? "rgba(184,85,48,0.55)"
                  : "var(--mt-line)",
                background: isCurrent
                  ? "rgba(184,85,48,0.13)"
                  : "rgba(245,239,228,0.025)",
                transition: "background 0.3s ease, border-color 0.3s ease",
              }}
            >
              <div
                className="shrink-0 mt-0.5 font-mono text-sm tabular-nums tracking-wide"
                style={{
                  color: isCurrent
                    ? "var(--mt-amber-bright)"
                    : "var(--mt-text-muted)",
                  fontWeight: isCurrent ? 700 : 500,
                  minWidth: 44,
                }}
              >
                {it.time}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className="font-display text-base leading-tight"
                    style={{
                      color: "var(--mt-cream)",
                      fontWeight: isCurrent ? 600 : 500,
                    }}
                  >
                    {it.title}
                  </span>
                  {isCurrent && (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-[0.18em]"
                      style={{
                        background: "rgba(184,85,48,0.95)",
                        color: "#fff",
                      }}
                    >
                      <span
                        className="mt-live-dot w-1 h-1 rounded-full"
                        style={{ background: "#fff" }}
                      />
                      In onda
                    </span>
                  )}
                  {it.note && (
                    <span
                      className="text-[9px] uppercase tracking-[0.22em] font-semibold"
                      style={{ color: "var(--mt-amber)" }}
                    >
                      {it.note}
                    </span>
                  )}
                </div>
                {it.subtitle && (
                  <div
                    className="text-[11px] mt-0.5 italic"
                    style={{ color: "var(--mt-amber-bright)" }}
                  >
                    {it.subtitle}
                  </div>
                )}
                {it.location && (
                  <div
                    className="flex items-center gap-1 text-[11px] mt-0.5"
                    style={{ color: "var(--mt-text-muted)" }}
                  >
                    <MapPin size={10} strokeWidth={2.2} />
                    <span className="truncate">{it.location}</span>
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {nextProgram && (
        <div
          className="mt-4 sm:mt-5 flex items-start gap-3 rounded-xl border px-4 py-3"
          style={{
            borderColor: "rgba(212,160,91,0.30)",
            background:
              "linear-gradient(135deg, rgba(212,160,91,0.10) 0%, rgba(212,160,91,0.04) 100%)",
          }}
          data-testid="schedule-next"
        >
          <div
            className="shrink-0 mt-0.5 w-9 h-9 rounded-full flex items-center justify-center"
            style={{
              background: "rgba(212,160,91,0.18)",
              color: "var(--mt-amber-bright)",
              border: "1px solid rgba(212,160,91,0.35)",
            }}
          >
            <Clock3 size={15} strokeWidth={2.2} />
          </div>
          <div className="flex-1 min-w-0">
            <div
              className="text-[10px] uppercase tracking-[0.28em] font-semibold"
              style={{ color: "var(--mt-amber-bright)" }}
            >
              In arrivo · {formatCountdown(nextProgram.minutesUntil)}
            </div>
            <div
              className="font-display text-base sm:text-lg leading-tight mt-0.5 truncate"
              style={{ color: "var(--mt-cream)" }}
              data-testid="schedule-next-title"
            >
              {nextProgram.item.time} · {nextProgram.item.title}
            </div>
            {nextProgram.item.subtitle && (
              <div
                className="text-[11px] italic mt-0.5"
                style={{ color: "var(--mt-amber)" }}
              >
                {nextProgram.item.subtitle}
              </div>
            )}
            {nextProgram.item.location && (
              <div
                className="text-[11px] mt-0.5"
                style={{ color: "var(--mt-text-muted)" }}
              >
                {nextProgram.item.location}
              </div>
            )}
          </div>
        </div>
      )}

      <div
        className="mt-4 sm:mt-5 pt-3 sm:pt-4 border-t text-[11px] flex items-center justify-between flex-wrap gap-2"
        style={{
          borderColor: "rgba(245,239,228,0.06)",
          color: "var(--mt-text-muted)",
        }}
      >
        <span>
          Fonte ufficiale:{" "}
          <a
            href={SOURCE_URL}
            target="_blank"
            rel="noreferrer"
            className="mt-link-underline"
            style={{ color: "var(--mt-cream)" }}
          >
            radiomadreterra.it
          </a>
          {weekdayName && (
            <span className="ml-1.5 opacity-70">· {weekdayName}</span>
          )}
        </span>
        <span className="opacity-70">
          Possibili variazioni in occasione di festività e celebrazioni
          straordinarie.
        </span>
      </div>
    </section>
  );
};

export default Schedule;
